import React from "react";
import { SvgComponent } from "../SvgComponent/SvgComponent";
import { AnimatedIconButton } from "../AnimatedIconButton/AnimatedIconButton";
import { StringHelper } from "../../Utils";
import "./CuentaSection.css";

const cleanValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return "No disponible";
  }

  return value;
};

const formatPhone = (phone, extension) => {
  if (!phone) {
    return "No disponible";
  }

  const formatted = StringHelper.formatPhoneNumber(phone);
  return extension ? `${formatted} Ext. ${extension}` : formatted;
};

const AccountIcon = ({ name, tone = "orange" }) => (
  <span className={`mi-cuenta-account-icon mi-cuenta-account-icon--${tone}`}>
    <SvgComponent iconName={name} width="22px" height="22px" fill="currentColor" />
  </span>
);

const getCompanyLogoSrc = (logo) => {
  if (typeof logo !== "string") {
    return null;
  }

  const value = logo.trim();

  if (!value) {
    return null;
  }

  if (/^data:image\//i.test(value) || /^https?:\/\//i.test(value) || value.startsWith("/")) {
    return value;
  }

  if (/^[A-Za-z0-9+/=\s]+$/.test(value) && value.replace(/\s/g, "").length > 64) {
    return `data:image/png;base64,${value.replace(/\s/g, "")}`;
  }

  return null;
};

const CompanyAvatar = ({ logo, name }) => {
  const [hasError, setHasError] = React.useState(false);
  const logoSrc = getCompanyLogoSrc(logo);

  if (!logoSrc || hasError) {
    return <SvgComponent iconName="id-card" width="46px" height="46px" fill="currentColor" />;
  }

  return <img src={logoSrc} alt={name || "Logo de la compania"} onError={() => setHasError(true)} />;
};

const Field = ({ label, value, wide }) => (
  <div className={`mi-cuenta-account-field ${wide ? "mi-cuenta-account-field--wide" : ""}`}>
    <span>{label}</span>
    <strong>{cleanValue(value)}</strong>
  </div>
);

const InfoCard = ({ icon, title, subtitle, children, tone = "orange", action }) => (
  <section className="mi-cuenta-account-card">
    <div className="mi-cuenta-account-card__header">
      <AccountIcon name={icon} tone={tone} />
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action && <div className="mi-cuenta-account-card__action">{action}</div>}
    </div>
    <div className="mi-cuenta-account-card__body">{children}</div>
  </section>
);

const DetailList = ({ details }) => {
  const visibleDetails = (details || []).filter((detail) => detail?.titulo || detail?.informacion);

  if (!visibleDetails.length) {
    return <p className="mi-cuenta-account-empty">No hay información registrada.</p>;
  }

  return (
    <div className="mi-cuenta-account-list">
      {visibleDetails.map((detail, index) => (
        <Field key={`${detail.titulo}-${index}`} label={detail.titulo || "Detalle"} value={detail.informacion} />
      ))}
    </div>
  );
};

export function CuentaSection({ cuentaInfo }) {
  const {
    compania,
    balance,
    configuracion = [],
    referidos,
    solicitudes,
    onEdit,
    onCrearSolicitud,
  } = cuentaInfo || {};
  const companiaLogoSrc = getCompanyLogoSrc(compania?.Logo);

  return (
    <div className="mi-cuenta-account">
      <main className="mi-cuenta-account-shell">
        <div className="mi-cuenta-account-page-title">
          <div>
            <h1>Información de compañía</h1>
            <p>Consulta la información general, facturación de servicio, estado de cuenta y solicitudes desde este panel.</p>
          </div>
          <AnimatedIconButton
            onClick={onEdit}
            iconName="edit"
            message="Editar"
            size="45px"
            height="24px"
            width="24px"
            id="mi-cuenta-edit-icon"
            style2={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "45px",
              height: "45px",
            }}
          />
        </div>

        <InfoCard
          icon={companiaLogoSrc ? "company" : "id-card"}
          title={compania?.Nombre || "Compañía"}
          subtitle="Información principal bloqueada hasta entrar en modo edición."
          tone="green"
        >
          <div className="mi-cuenta-account-profile">
            <div className="mi-cuenta-account-avatar">
              <CompanyAvatar logo={compania?.Logo} name={compania?.Nombre} />
            </div>
            <div className="mi-cuenta-account-grid">
              <Field label="Documento" value={compania?.RNC} />
              <Field label="Código" value={compania?.Codigo} />
              <Field label="Teléfono principal" value={formatPhone(compania?.TelefonoPrincipal, compania?.ExtensionTelefonoPrincipal)} />
              <Field label="Teléfono secundario" value={formatPhone(compania?.TelefonoSecundario, compania?.ExtensionTelefonoSecundario)} />
              <Field label="Correo" value={compania?.Email?.split(",")[0]} wide />
            </div>
          </div>
        </InfoCard>

        <div className="mi-cuenta-account-columns">
          <InfoCard icon="taxes" title="Facturación de Servicio" subtitle="Datos fiscales usados para emitir comprobantes.">
            <div className="mi-cuenta-account-grid">
              <Field label="Tipo de Comprobante" value={compania?.cliente?.Tipo} />
              <Field label="Tipo de Documento" value={compania?.cliente?.TipoDocumento} />
              <Field label="Documento" value={compania?.cliente?.Documento} />
              <Field label="Nombre" value={compania?.cliente?.Nombre} />
              <Field label="Teléfono Principal" value={compania?.cliente?.Telefono1} />
              <Field label="Email" value={compania?.cliente?.Email} />
            </div>
          </InfoCard>

          <InfoCard icon="balance" title="Estado de Cuenta" subtitle="Resumen del balance pendiente." tone="blue">
            <div className="mi-cuenta-account-total">
              <span>Balance pendiente</span>
              <strong>{cleanValue(balance)}</strong>
            </div>
          </InfoCard>
        </div>

        <InfoCard icon="setting" title="Configuración" subtitle="Servicios, licencias e integraciones activos.">
          <DetailList details={configuracion} />
        </InfoCard>

        <div className="mi-cuenta-account-columns">
          <InfoCard icon="referidos" title="Referidos" subtitle="Referidos registrados en tu cuenta." tone="blue">
            <p className="mi-cuenta-account-text">{referidos || "No hay referidos registrados."}</p>
          </InfoCard>

          <InfoCard icon="notes" title="Solicitudes" subtitle="Casos y solicitudes asociados a esta cuenta.">
            <p className="mi-cuenta-account-text">{solicitudes || "No hay solicitudes registradas."}</p>
            <button className="mi-cuenta-account-secondary-action" type="button" onClick={onCrearSolicitud}>
              <SvgComponent iconName="notes" width="18px" height="18px" fill="currentColor" />
              <span>Nueva solicitud</span>
            </button>
          </InfoCard>
        </div>
      </main>
    </div>
  );
}
